import {
  DEFAULT_PAGE_INDEX_INPUT,
  DEFAULT_PAGE_SIZE,
  REACT_APP_API_URL_REPORT,
} from "constants/index";

import { useCallback, useMemo } from "react";

import { Grid, IconButton, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro";
import { Download01Icon, EyeIcon } from "components";
import { Breadcrumbs } from "components/Breadcrumbs/Breadcrumbs";
import { Table } from "components/Table/Table";
import { confirm } from "contexts";
import saveAs from "file-saver";
import { useReportDistributionControllerGetListReportDistribution } from "generated/rm";
import { useNotification } from "hooks/useNotification";
import { useSearchParams, useUpdateSearch } from "hooks/useSearchParams";
import { useLocation, useNavigate } from "react-router-dom";
import { NumberParam, useQueryParams, withDefault } from "use-query-params";
import storage from "utils/storage";

import { fieldManagementColumn } from "./columns";
import { ReportDistributionRelation } from "./interface";
import { ReportManagementFilter } from "./ReportManagementFilter";

export const ReportManagementList = () => {
  const { search, pathname } = useLocation();
  const { handleSearchClick } = useUpdateSearch(pathname, search);
  const searchParams = useSearchParams(search);
  const [query, setQuery] = useQueryParams({
    pageIndex: withDefault(NumberParam, DEFAULT_PAGE_INDEX_INPUT),
    pageSize: withDefault(NumberParam, DEFAULT_PAGE_SIZE),
  });
  const { pageIndex, pageSize } = query;

  const navigate = useNavigate();
  const { data, isLoading, refetch, error } =
    // eslint-disable-next-line
    useReportDistributionControllerGetListReportDistribution(
      {
        ...(searchParams?.assignedDate &&
        searchParams?.assignedDate?.length > 0 &&
        searchParams?.assignedDate[1] !== null
          ? {
              dateTo: searchParams?.assignedDate[1],
            }
          : {}),
        ...(searchParams?.assignedDate &&
        searchParams?.assignedDate?.length > 0 &&
        searchParams?.assignedDate[0] !== null
          ? {
              dateFrom: searchParams?.assignedDate[0],
            }
          : {}),
        name: searchParams?.name,
        skip: (pageIndex - 1) * pageSize,
        take: pageSize,
      },
      {
        axios: {
          baseURL: REACT_APP_API_URL_REPORT,
        },
      }
    );
  const handleDownloadTemplate = useCallback(
    (id: string, nameFile?: string | null) => {
      confirm({ title: "Download", content: "Do you want to download?" })
        .then(() => {
          const fileUrl = `${REACT_APP_API_URL_REPORT}/report-distribution/download?distributionId=${id}`;

          const token = storage.getToken();

          const headers = new Headers();
          headers.append("Authorization", `Bearer ${token}`);

          fetch(fileUrl, { headers })
            .then((response) => response.blob())
            .then((blob) => {
              saveAs(blob, nameFile ?? "download");
            })
            .catch((e) => {});
        })
        .catch(() => {});
    },
    []
  );

  const columns = useMemo<GridColDef<ReportDistributionRelation>[]>(() => {
    return [
      ...fieldManagementColumn,
      {
        field: "id",
        headerName: "Action",
        renderCell: (params) => {
          return (
            <Grid container>
              <Grid
                item
                xs={6}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <Tooltip title="View">
                  <IconButton
                    disabled={
                      new Date(params?.row?.expiredAt as string) < new Date()
                    }
                    onClick={() => {
                      navigate(
                        `/report-distribution/detail/${
                          params?.row?.id as string
                        }`
                      );
                    }}
                  >
                    <EyeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Grid>
              {params?.row?.allowDownload ? (
                <Grid
                  item
                  xs={6}
                  sx={{ display: "flex", justifyContent: "center" }}
                >
                  <Tooltip title="Download">
                    <IconButton
                      disabled={
                        new Date(params?.row?.expiredAt as string) < new Date()
                      }
                      onClick={() => {
                        handleDownloadTemplate(
                          params?.row?.id as string,
                          params?.row?.originalFileName
                        );
                      }}
                    >
                      <Download01Icon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Grid>
              ) : null}
            </Grid>
          );
        },
        width: 118,
      },
    ];
  }, [handleDownloadTemplate, navigate]);
  useNotification(error);
  return (
    <>
      <Breadcrumbs
      // rightComponent={
      //   <Button
      //     // onClick={() => {
      //     //   setIsModalCreateOpen(true);
      //     // }}
      //     startIcon={<PlusIcon />}
      //   >
      //     Create new
      //   </Button>
      // }
      />
      <Table
        isLoading={isLoading}
        columns={columns}
        rows={data?.data?.data ?? []}
        isPaginationModal={false}
        pageSizeOptions={[10, 50]}
        paginationMode="server"
        paginationModel={{
          page: pageIndex - 1,
          pageSize,
        }}
        rowCount={data?.data?.totalCount ?? 0}
        filterForm={
          <ReportManagementFilter
            data={searchParams}
            onSearch={handleSearchClick}
          />
        }
      />
    </>
  );
};
